/**
 * FG Stock Book · Mar 3, 2026
 *
 * Chronological FG ledger per product — every pack event (Inward)
 * and every DC line (Outward) with running balance. Sourced from
 * `GET /api/reports/fg-stock-book?article=<code>`.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

export default function FGStockBook() {
  const [params] = useSearchParams();
  const [article, setArticle] = useState(params.get("article") || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/fg-stock-book", {
        params: article ? { article } : {},
      });
      setRows(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load FG stock book"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [article]);

  const totals = useMemo(() => rows.reduce((a, r) => ({
    in: a.in + Number(r.in_qty || 0),
    out: a.out + Number(r.out_qty || 0),
  }), { in: 0, out: 0 }), [rows]);

  return (
    <div className="space-y-6" data-testid="fg-stock-book">
      <PageHeader
        overline="Sales · FG Inventory"
        title={`FG Stock Book${article ? ` · ${article}` : ""}`}
        subtitle="Chronological movement for each finished product — Inward from Packing Slips, Outward via Delivery Challans, with a running balance so operators trace exactly how each article's stock evolved."
        actions={
          <Link
            to="/sales/fg-stock-register"
            className="h-9 px-4 rounded-sm border border-[var(--border)] bg-[var(--bg-1)] text-xs font-mono uppercase tracking-widest text-zinc-300 hover:border-[var(--accent)] hover:text-[var(--accent)] flex items-center"
          >← FG Stock Register</Link>
        }
      />

      <div className="grid grid-cols-3 gap-3">
        {[
          ["∑ Inward",   fmt(totals.in),  "text-emerald-300"],
          ["∑ Outward",  fmt(totals.out), "text-red-300"],
          ["Net",        fmt(totals.in - totals.out), "text-white"],
        ].map(([l, v, c]) => (
          <div key={l} className="border border-[var(--border)] bg-[var(--bg-1)] rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
            <div className={`text-2xl font-mono tabular-nums mt-1 ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      <input
        data-testid="fgsb-article"
        value={article}
        onChange={e => setArticle(e.target.value)}
        placeholder="Filter by product code (blank = all)…"
        className="h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm font-mono text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[var(--accent)] w-96"
      />

      <div className="border border-[var(--border)] rounded-sm overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/30 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-3 py-2 w-28">Date</th>
              <th className="text-left px-3 py-2 w-20">Type</th>
              <th className="text-left px-3 py-2 w-40">Doc No</th>
              <th className="text-left px-3 py-2">Product</th>
              <th className="text-left px-3 py-2 w-24">Box No</th>
              <th className="text-right px-3 py-2 w-24">In</th>
              <th className="text-right px-3 py-2 w-24">Out</th>
              <th className="text-right px-3 py-2 w-24">Balance</th>
              <th className="text-left px-3 py-2 w-32">Ref</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-3 py-6 text-center text-zinc-500 text-xs font-mono uppercase tracking-widest">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-zinc-500 text-xs">No movements</td></tr>}
            {!loading && rows.map((r, i) => {
              const isIn = r.doc_type === "PACK";
              return (
                <tr key={i} data-testid={`fgsb-row-${i}`} className="border-t border-[var(--border)]/60 hover:bg-[var(--bg-2)]/20">
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-400">{r.date || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-1.5 py-0.5 rounded-sm border font-mono text-[9px] uppercase tracking-widest ${isIn ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" : "border-red-500/40 bg-red-500/5 text-red-300"}`}>
                      {r.doc_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.doc_no || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-300">{r.product_code || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-zinc-500">{r.box_no || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300">{r.in_qty > 0 ? fmt(r.in_qty) : ""}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-red-300">{r.out_qty > 0 ? fmt(r.out_qty) : ""}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-white">{fmt(r.balance)}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-500">{r.ref || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
